import { createContext } from 'react';

import { USER_RIGHTS } from '@crm-package/auth';
export { EMPTY_VALUE } from '@crm-framework/utils';

export const IsHorizontalContext = createContext<undefined | boolean>(
  undefined
);

export const NEW_LEAD_ENTITY_NAME = 'new';

export const LEAD_FORM_NAME = 'lead';
export const FIRST_NAME = 'firstName';
export const LAST_NAME = 'lastName';
export const COUNTRY = 'countryId';
export const STATE = 'stateId';
export const TIME_ZONE = 'timeZone';
export const CITY = 'city';
export const LANGUAGE = 'languageId';
export const EMAIL = 'email';
export const SKYPE = 'skype';
export const TELEGRAM = 'telegram';
export const BLOCK_LOGIN = 'isLoginToOriginalDomainBlocked';

// Fields CRM Data
export const BIZ_GROUP = 'businessUnitId';
export const CONVERSION_STATUS = 'salesStatusId';
export const IS_QUALIFIED = 'isEligible';
export const REJECT_REASON_FOR_NO_POTENTIAL = 'salesStatusRejectReasonId';
export const REJECT_REASON_FOR_NOT_VALID = 'eligibilityRejectReasonId';
export const CONTACT_TIME = 'contactTime';
export const FULLY_ACTIVATION_DATE = 'fullyActivationDate';
export const LABELS = 'leadTags';
export const ALLOWED_TO_WITHDRAW = 'allowedToWithdraw';
export const ALLOW_CC_DEPOSIT = 'allowedToCardDeposit';
export const COMMENT = 'operationalComment';
export const TRADING_STATUS = 'tradingStatusId';
export const ACCOUNT_MANAGER = 'conversionAgentId';
export const RET_ACC_MANAGER = 'retentionManagerId';
export const VIP_MANAGER = 'vipManagerId';
export const CLOSER_MANAGER = 'closerManagerId';

export const PHONE_AMOUNT = 3;

export const CONTAINER_GRID_PROPS = {
  columns: '1fr 1fr',
  columnGap: '15px',
  minRowHeight: '45px',
};

export const INNER_GRID_PROPS = {
  columns: '130px 1fr',
  columnGap: '10px',
  rowGap: '5px',
  alignContent: 'start',
};

export const LEAD_FORM_GRID_PROPS = {
  ...INNER_GRID_PROPS,
  columns: '150px 400px',
};

export enum LeadDetailsDictionaries {
  timeZone = 'timeZone',
  countryStates = 'countryStates',
  currentStateDictionary = 'currentStateDictionary',
  businessUnit = 'businessUnit',
  closerManagerTeam = 'closerManagerTeam',
  country = 'country',
  environment = 'environment',
  language = 'language',
  vipManager = 'vipManager',
  businessUnitsByEnvironment = 'businessUnitsByEnvironment',
  salesStatus = 'salesStatus',
  availableSalesStatuses = 'availableSalesStatuses',
  salesStatusRejectReason = 'salesStatusRejectReason',
  eligibilityRejectReason = 'eligibilityRejectReason',
  tradingStatus = 'tradingStatus',
  conversionAgent = 'conversionAgent',
  retentionManager = 'retentionManager',
  closerManager = 'closerManager',
  leadTag = 'leadTag',
  salesManager = 'salesManager',
  historyType = 'historyType',
  currencies = 'currencies',
}

export const departmentByDictionaryName = {
  [LeadDetailsDictionaries.vipManager]: 'vip',
  [LeadDetailsDictionaries.conversionAgent]: 'sales',
  [LeadDetailsDictionaries.retentionManager]: 'retention',
  [LeadDetailsDictionaries.closerManager]: 'closer',
};

// Conversion statuses
export const DICTIONARY_EMPTY_VALUE_ID = 0;

export const STATUS_NEW = 'New';
export const STATUS_NO_POTENTIAL = 'No Potential';
export const STATUS_NOT_VALID = 'Not Valid';
export const STATUS_FULLY_ACTIVATED = 'Fully Activated';

export const LEAD_REGISTRY = {
  mainFormLoading: 'main-form-loading',
  leadHistory: 'lead-history',
};

export const LEAD_PREVIEW_MODAL = 'lead-preview-modal';
export const CONTACT_DATA_MODAL = 'contact-data-modal';
export const CONTACT_DATA_FORM = 'contact-data-form';

export enum leadDetailsRightsType {
  crmChatRights = 'crmChatRights',
  changeBrandRights = 'changeBrandRights',
  resetTwoFARights = 'resetTwoFARights',
  editFullNameRights = 'editFullNameRights',
  changePasswordRights = 'changePasswordRights',
  editDetailsRights = 'editDetailsRights',
  editCommentRights = 'editCommentRights',
  deleteCommentRights = 'deleteCommentRights',
  editManagerRights = 'editManagerRights',
  editCloserManagerRights = 'editCloserManagerRights',
  viewPhonesRights = 'viewPhonesRights',
  editPhonesRights = 'editPhonesRights',
  phoneVerificationRights = 'phoneVerificationRights',
  viewPhoneVerificationStatusRights = 'viewPhoneVerificationStatusRights',
  changeBURights = 'changeBURights',
  fullyActivationDateRights = 'fullyActivationDateRights',
  viewEmailRights = 'viewEmailRights',
  editEmailRights = 'editEmailRights',
  hidingEmailRights = 'hidingEmailRights',
}

export const LEAD_DETAILS_RIGHTS = {
  [leadDetailsRightsType.crmChatRights]: [USER_RIGHTS.ALLOW_CHAT],
  [leadDetailsRightsType.changeBrandRights]: [USER_RIGHTS.CHANGE_BRAND],
  [leadDetailsRightsType.resetTwoFARights]: [USER_RIGHTS.RESET_2FA],
  [leadDetailsRightsType.editFullNameRights]: [USER_RIGHTS.CLIENT_NAME_EDIT],
  [leadDetailsRightsType.changePasswordRights]: [USER_RIGHTS.PASSWORD_EDIT],
  [leadDetailsRightsType.editDetailsRights]: [
    USER_RIGHTS.DETAILS_EDIT_ALL,
    USER_RIGHTS.DETAILS_EDIT_ASSIGNED,
  ],
  [leadDetailsRightsType.editManagerRights]: [
    USER_RIGHTS.ALLOW_NONDEPARTMENT_ASSIGNMENT,
  ],
  [leadDetailsRightsType.editCloserManagerRights]: [
    USER_RIGHTS.ALLOW_CLOSER_ASSIGNMENT,
  ],
  [leadDetailsRightsType.viewPhonesRights]: [
    USER_RIGHTS.DETAILS_VIEW_PHONE_ALL,
    USER_RIGHTS.DETAILS_VIEW_PHONE_ASSIGNED,
  ],
  [leadDetailsRightsType.editPhonesRights]: [
    USER_RIGHTS.DETAILS_EDIT_PHONE_ALL,
    USER_RIGHTS.DETAILS_EDIT_PHONE_ASSIGNED,
  ],
  [leadDetailsRightsType.phoneVerificationRights]: [
    USER_RIGHTS.ALLOW_HLR_PHONE_VERIFICATION,
  ],
  [leadDetailsRightsType.viewPhoneVerificationStatusRights]: [
    USER_RIGHTS.VIEW_HLR_PHONE_VERIFICATION_STATUS,
  ],
  [leadDetailsRightsType.changeBURights]: [
    USER_RIGHTS.ALLOW_ASSIGNMENT_BIZ_GROUP,
  ],
  [leadDetailsRightsType.fullyActivationDateRights]: [
    USER_RIGHTS.FULLY_ACTIVATION_DATE_EDIT,
  ],
  [leadDetailsRightsType.editEmailRights]: [USER_RIGHTS.EDIT_EMAIL],
  [leadDetailsRightsType.viewEmailRights]: [
    USER_RIGHTS.VIEW_EMAIL_ALL,
    USER_RIGHTS.VIEW_EMAIL_ASSIGNED,
  ],
  [leadDetailsRightsType.hidingEmailRights]: [
    USER_RIGHTS.OLD_EMAIL_POLITICS_ALL,
  ],
  [leadDetailsRightsType.editCommentRights]: [USER_RIGHTS.EDIT_MANAGER_COMMENT],
  [leadDetailsRightsType.deleteCommentRights]: [
    USER_RIGHTS.DELETE_MANAGER_COMMENT,
  ],
};

export const CUSTOM_EMPTY_VALUE = { id: 0, name: '< Empty >', order: 0 };
